import React, { useEffect, Children } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Heart, Sparkles, Clock, Shield, ShieldCheck, Gift, Zap } from 'lucide-react';
import { SalesNotification } from '../components/SalesNotification';
import { initMetaPixel, trackPageView, trackAddToCart } from '../utils/metaPixel';
export function SalesPage() {
  // Initialize Meta Pixel and fire PageView on load
  useEffect(() => {
    initMetaPixel();
    trackPageView();
  }, []);
  const fadeInUp = {
    initial: {
      opacity: 0,
      y: 30
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true,
      margin: '-100px'
    },
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  };
  const staggerContainer = {
    initial: {},
    whileInView: {
      transition: {
        staggerChildren: 0.1
      }
    },
    viewport: {
      once: true,
      margin: '-100px'
    }
  };
  const handleCheckout = () => {
    // Fire AddToCart event before redirecting
    trackAddToCart();
    try {
      window.location.href = 'https://www.conesenciavital.com/cart/47255503601903:1';
    } catch (error) {
      window.open('https://www.conesenciavital.com/cart/47255503601903:1', '_blank');
    }
  };
  return <div className="w-full bg-[var(--color-cream)] min-h-screen" data-id="element-12">
      {/* Sales Notification - Mobile Only */}
      <SalesNotification data-id="element-13" />

      {/* Hero Section */}
      <section className="relative px-6 py-16 md:py-24 max-w-6xl mx-auto" data-id="element-14">
        <motion.div className="text-center mb-12" initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8
      }} data-id="element-15">
          <motion.p className="text-white bg-[var(--color-coral)] inline-block px-6 py-2 rounded-full font-medium mb-4 tracking-wide uppercase text-sm" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.2
        }} data-id="element-16">
            Para la mujer que ha intentado todo
          </motion.p>
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-[var(--color-navy)] mb-6 leading-tight" data-id="element-17">
            No es gordura,
            <br data-id="element-18" />
            es inflamación
          </h1>
          <p className="text-lg md:text-xl text-[var(--color-navy-light)] max-w-2xl mx-auto leading-relaxed" data-id="element-19">
            Despídete del vientre inflamado en solo 7 días con el método que
            desactiva la inflamación desde adentro
          </p>
        </motion.div>

        <motion.div className="flex justify-center mb-12" initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        delay: 0.4,
        duration: 0.6
      }} data-id="element-20">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/m2p3AGDpKYj8nvdLW5z1F8/foto_del_producto.png" alt="Guía No es Gordura, es Inflamación" className="w-full max-w-md rounded-lg shadow-2xl" data-id="element-21" />
        </motion.div>

        <motion.div className="text-center" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.6
      }} data-id="element-22">
          <button type="button" onClick={e => {
          e.preventDefault();
          handleCheckout();
        }} className="bg-[var(--color-coral)] hover:bg-[var(--color-coral-dark)] text-white font-semibold px-12 py-5 rounded-full text-lg transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105" data-id="element-23">
            Quiero Desinflamarme Ahora
          </button>
          <p className="text-sm text-[var(--color-navy-light)] mt-4" data-id="element-24">
            Resultados visibles en 7 días • Sin restricciones extremas
          </p>
        </motion.div>
      </section>

      {/* Problem Section */}
      <section className="bg-[var(--color-warm-beige)] px-6 py-20" data-id="element-25">
        <div className="max-w-4xl mx-auto" data-id="element-26">
          <motion.h2 className="text-3xl md:text-5xl font-bold text-[var(--color-navy)] text-center mb-12" {...fadeInUp} data-id="element-27">
            ¿Te sientes identificada?
          </motion.h2>

          <motion.div className="space-y-6" variants={staggerContainer} initial="initial" whileInView="whileInView" viewport={{
          once: true
        }} data-id="element-28">
            {['Te miras al espejo y ves un vientre que no refleja el esfuerzo que haces', 'Comes sano, evitas antojos y aun así amaneces inflamada', 'Has probado dietas sin éxito', 'Te sientes hinchada sin explicación lógica', 'Vives con un abdomen duro, pesado y molesto'].map((item, index) => <motion.div key={index} className="flex items-start gap-4 bg-white p-6 rounded-xl shadow-sm" variants={fadeInUp} data-id="element-29">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[var(--color-coral)] bg-opacity-20 flex items-center justify-center mt-1" data-id="element-30">
                  <div className="w-2 h-2 rounded-full bg-[var(--color-coral)]" data-id="element-31" />
                </div>
                <p className="text-[var(--color-navy)] text-lg" data-id="element-32">{item}</p>
              </motion.div>)}
          </motion.div>

          <motion.div className="mt-16 text-center" {...fadeInUp} data-id="element-33">
            <p className="text-2xl md:text-3xl font-semibold text-[var(--color-navy)] mb-4" data-id="element-34">
              No es tu culpa.
            </p>
            <p className="text-xl text-[var(--color-navy-light)] leading-relaxed" data-id="element-35">
              No es grasa. No es tu edad. No es tu metabolismo.
              <br data-id="element-36" />
              <span className="text-[var(--color-coral)] font-semibold" data-id="element-37">
                Es inflamación.
              </span>
            </p>
          </motion.div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="px-6 py-20 max-w-5xl mx-auto" data-id="element-38">
        <motion.div className="text-center mb-16" {...fadeInUp} data-id="element-39">
          <h2 className="text-3xl md:text-5xl font-bold text-[var(--color-navy)] mb-6" data-id="element-40">
            La solución que estabas buscando
          </h2>
          <p className="text-xl text-[var(--color-navy-light)] max-w-3xl mx-auto leading-relaxed" data-id="element-41">
            Una guía práctica de 7 días basada en nutrición antiinflamatoria,
            manejo del estrés y hábitos estratégicos para desinflamar tu abdomen
            y recuperar tu energía.
          </p>
        </motion.div>

        <motion.div className="grid md:grid-cols-2 gap-8" variants={staggerContainer} initial="initial" whileInView="whileInView" viewport={{
        once: true
      }} data-id="element-42">
          {[{
          icon: Heart,
          title: 'Diagnóstico de inflamación',
          desc: 'Identifica las causas reales de tu hinchazón'
        }, {
          icon: Sparkles,
          title: 'Menú antiinflamatorio de 7 días',
          desc: 'Recetas deliciosas que desactivan la inflamación'
        }, {
          icon: Clock,
          title: 'Rituales matutinos y nocturnos',
          desc: 'Hábitos simples que transforman tu día'
        }, {
          icon: Shield,
          title: 'Protocolos de emergencia',
          desc: 'Soluciones rápidas para días de hinchazón'
        }].map((item, index) => <motion.div key={index} className="bg-white p-8 rounded-2xl shadow-md" variants={fadeInUp} data-id="element-43">
              <div className="w-14 h-14 bg-[var(--color-coral)] bg-opacity-10 rounded-full flex items-center justify-center mb-6" data-id="element-44">
                <item.icon className="w-7 h-7 text-[var(--color-coral)]" data-id="element-45" />
              </div>
              <h3 className="text-xl font-semibold text-[var(--color-navy)] mb-3" data-id="element-46">
                {item.title}
              </h3>
              <p className="text-[var(--color-navy-light)]" data-id="element-47">{item.desc}</p>
            </motion.div>)}
        </motion.div>

        <motion.div className="mt-12 bg-gradient-to-br from-[var(--color-coral)] to-[var(--color-coral-dark)] p-8 md:p-12 rounded-2xl text-white text-center" {...fadeInUp} data-id="element-48">
          <p className="text-2xl md:text-3xl font-semibold mb-4" data-id="element-49">
            Este método funciona porque no busca que comas menos,
          </p>
          <p className="text-3xl md:text-4xl font-bold" data-id="element-50">
            sino que desactives la inflamación interna.
          </p>
        </motion.div>
      </section>

      {/* Benefits Section */}
      <section className="bg-[var(--color-warm-beige)] px-6 py-20" data-id="element-51">
        <div className="max-w-5xl mx-auto" data-id="element-52">
          <motion.h2 className="text-3xl md:text-5xl font-bold text-[var(--color-navy)] text-center mb-16" {...fadeInUp} data-id="element-53">
            Lo que vas a experimentar
          </motion.h2>

          <motion.div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" variants={staggerContainer} initial="initial" whileInView="whileInView" viewport={{
          once: true
        }} data-id="element-54">
            {['Abdomen más plano desde el día 3', 'Digestión ligera y regular', 'Menos gases, menos pesadez, menos retención', 'Más energía y claridad mental', 'Menos ansiedad y menos antojos', 'Sueño profundo y reparador', 'Inflamación articular reducida', 'Cambios visibles en 7 días', 'Recuperas tu autoestima'].map((benefit, index) => <motion.div key={index} className="flex items-start gap-3 bg-white p-6 rounded-xl shadow-sm" variants={fadeInUp} data-id="element-55">
                <CheckCircle2 className="w-6 h-6 text-[var(--color-coral)] flex-shrink-0 mt-0.5" data-id="element-56" />
                <p className="text-[var(--color-navy)] font-medium" data-id="element-57">
                  {benefit}
                </p>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="px-6 py-20 max-w-5xl mx-auto" data-id="element-58">
        <motion.h2 className="text-3xl md:text-5xl font-bold text-[var(--color-navy)] text-center mb-16" {...fadeInUp} data-id="element-59">
          Historias reales de transformación
        </motion.h2>

        <motion.div className="grid md:grid-cols-3 gap-8" variants={staggerContainer} initial="initial" whileInView="whileInView" viewport={{
        once: true
      }} data-id="element-60">
          {[{
          quote: 'Volví a reconocer mi cuerpo.',
          name: 'María, 34 años'
        }, {
          quote: 'Lloré cuando me puse mis jeans favoritos.',
          name: 'Carmen, 41 años'
        }, {
          quote: 'Mi ansiedad bajó y mi energía subió.',
          name: 'Laura, 38 años'
        }].map((testimonial, index) => <motion.div key={index} className="bg-white p-8 rounded-2xl shadow-md" variants={fadeInUp} data-id="element-61">
              <div className="text-[var(--color-coral)] text-5xl mb-4" data-id="element-62">"</div>
              <p className="text-lg text-[var(--color-navy)] mb-6 italic" data-id="element-63">
                {testimonial.quote}
              </p>
              <p className="text-[var(--color-navy-light)] font-medium" data-id="element-64">
                — {testimonial.name}
              </p>
            </motion.div>)}
        </motion.div>
      </section>

      {/* Objections Section */}
      <section className="bg-[var(--color-warm-beige)] px-6 py-20" data-id="element-65">
        <div className="max-w-4xl mx-auto" data-id="element-66">
          <motion.h2 className="text-3xl md:text-5xl font-bold text-[var(--color-navy)] text-center mb-16" {...fadeInUp} data-id="element-67">
            Preguntas frecuentes
          </motion.h2>

          <motion.div className="space-y-6" variants={staggerContainer} initial="initial" whileInView="whileInView" viewport={{
          once: true
        }} data-id="element-68">
            {[{
            q: '¿Y si no funciona para mí?',
            a: 'Este método actúa sobre mecanismos universales de inflamación. No es una dieta de moda, es ciencia aplicada a tu cuerpo.'
          }, {
            q: 'No tengo tiempo para seguir un programa complicado',
            a: 'Está diseñado para mujeres ocupadas. Es práctico, rápido y se adapta a tu rutina diaria sin complicaciones.'
          }, {
            q: 'No quiero restringir mi alimentación',
            a: 'No se trata de restricción, sino de regulación. Comes alimentos deliciosos que nutren y desinfaman tu cuerpo.'
          }].map((item, index) => <motion.div key={index} className="bg-white p-8 rounded-xl shadow-sm" variants={fadeInUp} data-id="element-69">
                <h3 className="text-xl font-semibold text-[var(--color-navy)] mb-3" data-id="element-70">
                  {item.q}
                </h3>
                <p className="text-[var(--color-navy-light)] leading-relaxed" data-id="element-71">
                  {item.a}
                </p>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* Purchase Section */}
      <section id="precio" className="px-6 py-20 max-w-6xl mx-auto scroll-mt-20" data-id="element-72">
        <motion.div className="bg-gradient-to-br from-[var(--color-coral)] to-[var(--color-coral-dark)] rounded-3xl overflow-hidden shadow-2xl" {...fadeInUp} data-id="element-73">
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center p-8 md:p-16" data-id="element-74">
            {/* Product Image */}
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} data-id="element-75">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/88b6MGeamcwJ9ESt9hTvAC/book-mockup.png" alt="Guía Digital - No es Gordura, es Inflamación" className="w-full max-w-md mx-auto drop-shadow-2xl" data-id="element-76" />
            </motion.div>

            {/* Purchase Info */}
            <motion.div className="text-white" initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: 0.3
          }} data-id="element-77">
              <h2 className="text-3xl md:text-5xl font-bold mb-6" data-id="element-78">
                Comienza tu transformación hoy
              </h2>

              <p className="text-xl mb-8 opacity-95 leading-relaxed" data-id="element-79">
                Acceso inmediato a la guía completa de 7 días para desinflamar
                tu abdomen y recuperar tu energía.
              </p>

              <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-2xl p-6 mb-8" data-id="element-80">
                <p className="text-lg mb-2 opacity-90" data-id="element-81">
                  Precio especial de lanzamiento:
                </p>
                <div className="flex items-baseline gap-3" data-id="element-82">
                  <span className="text-6xl font-bold" data-id="element-83">$9.97</span>
                  <span className="text-xl opacity-75" data-id="element-84">USD</span>
                </div>
                <p className="text-sm mt-2 opacity-90" data-id="element-85">
                  Pago único • Acceso de por vida
                </p>
              </div>

              <div className="space-y-4 mb-8" data-id="element-86">
                {['Guía completa de 7 días en formato digital', 'Menú antiinflamatorio con recetas', 'Rituales matutinos y nocturnos', 'Protocolos de emergencia', 'Acceso inmediato después del pago'].map((feature, index) => <div key={index} className="flex items-start gap-3" data-id="element-87">
                    <CheckCircle2 className="w-6 h-6 flex-shrink-0 mt-0.5" data-id="element-88" />
                    <p className="text-lg" data-id="element-89">{feature}</p>
                  </div>)}
              </div>

              <button type="button" onClick={e => {
              e.preventDefault();
              handleCheckout();
            }} className="w-full bg-white text-[var(--color-coral)] font-bold px-12 py-6 rounded-full text-xl transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:scale-105 mb-6" data-id="element-90">
                Comprar Ahora por $9.97
              </button>

              <div className="flex items-center justify-center gap-2 text-sm opacity-90" data-id="element-91">
                <ShieldCheck className="w-5 h-5" data-id="element-92" />
                <p data-id="element-93">Pago 100% seguro • Garantía de satisfacción</p>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* Bonus #1 Section */}
      <section className="px-6 py-20 max-w-6xl mx-auto" data-id="element-94">
        <motion.div className="text-center mb-12" {...fadeInUp} data-id="element-95">
          <div className="inline-flex items-center gap-2 bg-[var(--color-coral)] text-white px-6 py-3 rounded-full font-bold text-lg mb-6" data-id="element-96">
            <Gift className="w-6 h-6" data-id="element-97" />
            <span data-id="element-98">BONO INCLUIDO</span>
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-[var(--color-navy)] mb-4" data-id="element-99">
            Menú Express de 24 Horas
          </h2>
          <p className="text-2xl text-[var(--color-coral)] font-semibold mb-12" data-id="element-100">
            Tu rescate inmediato cuando amaneces hinchada
          </p>

          {/* Bonus Image */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="mb-12" data-id="element-101">
            <img src="https://cdn.magicpatterns.com/uploads/8dNrSAuCfh9737y14fxdzD/Mockup_en_formato_1x.png" alt="Menú Anti-Inflamación Express de 24 Horas" className="w-full max-w-lg mx-auto rounded-xl shadow-2xl" data-id="element-102" />
          </motion.div>
        </motion.div>

        <motion.div className="bg-white rounded-3xl shadow-xl overflow-hidden p-8 md:p-12 max-w-4xl mx-auto" {...fadeInUp} data-id="element-103">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.3
        }} data-id="element-104">
            <div className="bg-[var(--color-coral)] rounded-2xl p-8 mb-8" data-id="element-105">
              <p className="text-lg text-white leading-relaxed" data-id="element-106">
                ¿Despertaste con el abdomen duro, inflamado o tipo globo?
              </p>
              <p className="text-lg text-white mt-4 leading-relaxed" data-id="element-107">
                Este bono es tu plan de acción rápido para desinflamarte en un
                solo día, sentirte más ligera y recuperar tu energía sin dietas
                extremas.
              </p>
            </div>

            <h3 className="text-2xl font-bold text-[var(--color-navy)] mb-6" data-id="element-108">
              Con este menú tendrás:
            </h3>

            <div className="space-y-4 mb-8" data-id="element-109">
              {['Un plan completo de 24 horas (desayuno, comida, cena y snacks)', 'Horarios estratégicos que reducen inflamación y aceleran la digestión', 'Versión estándar + versión sin lácteos y sin gluten', 'Tips para comer fuera sin arruinar tu progreso', 'Hábitos exprés que potencian resultados (estrés, sueño, hidratación)', 'Checklist paso a paso para seguirlo sin pensar'].map((feature, index) => <div key={index} className="flex items-start gap-3" data-id="element-110">
                  <Sparkles className="w-5 h-5 text-[var(--color-coral)] flex-shrink-0 mt-1" data-id="element-111" />
                  <p className="text-[var(--color-navy-light)]" data-id="element-112">{feature}</p>
                </div>)}
            </div>

            <div className="bg-[var(--color-warm-beige)] rounded-2xl p-6 mb-8" data-id="element-113">
              <h4 className="text-xl font-bold text-[var(--color-navy)] mb-4 flex items-center gap-2" data-id="element-114">
                <Zap className="w-6 h-6 text-[var(--color-coral)]" data-id="element-115" />
                Ideal para:
              </h4>
              <div className="grid grid-cols-2 gap-3" data-id="element-116">
                {['Días de emergencia', 'Eventos importantes', 'Ropa ajustada', 'Reseteos semanales', 'Después de excesos', 'Viajes'].map((use, index) => <div key={index} className="flex items-center gap-2" data-id="element-117">
                    <CheckCircle2 className="w-4 h-4 text-[var(--color-coral)] flex-shrink-0" data-id="element-118" />
                    <p className="text-sm text-[var(--color-navy)]" data-id="element-119">{use}</p>
                  </div>)}
              </div>
            </div>

            <div className="bg-gradient-to-r from-[var(--color-coral)] to-[var(--color-coral-dark)] text-white p-6 rounded-2xl text-center" data-id="element-120">
              <p className="text-xl font-semibold" data-id="element-121">
                En 24 horas puedes desinflamar tu vientre, mejorar tu digestión
                y sentirte mucho mejor en tu piel.
              </p>
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* Bonus #2 Section */}
      <section className="px-6 py-20 max-w-6xl mx-auto" data-id="element-122">
        <motion.div className="text-center mb-12" {...fadeInUp} data-id="element-123">
          <div className="inline-flex items-center gap-2 bg-[var(--color-coral)] text-white px-6 py-3 rounded-full font-bold text-lg mb-6" data-id="element-124">
            <Heart className="w-6 h-6" data-id="element-125" />
            <span data-id="element-126">BONO ULTRA ESPECIAL</span>
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-[var(--color-navy)] mb-4" data-id="element-127">
            30 Snacks Anti-Inflamación
          </h2>
          <p className="text-2xl text-[var(--color-coral)] font-semibold mb-12" data-id="element-128">
            Para la mujer que quiere sentir ligereza, control y paz en su
            cuerpo… incluso en sus días más ocupados
          </p>

          {/* Bonus Image */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="mb-12" data-id="element-129">
            <img src="https://cdn.magicpatterns.com/uploads/sdz4VgvouHXswrC8QCmFvw/Mockup_cuadrado_(1x1.png" alt="30 Snacks Anti-Inflamación" className="w-full max-w-lg mx-auto rounded-xl shadow-2xl" data-id="element-130" />
          </motion.div>
        </motion.div>

        <motion.div className="bg-white rounded-3xl shadow-xl overflow-hidden p-8 md:p-12 max-w-4xl mx-auto" {...fadeInUp} data-id="element-131">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.3
        }} data-id="element-132">
            <div className="bg-[var(--color-coral)] rounded-2xl p-8 mb-8" data-id="element-133">
              <p className="text-lg text-white leading-relaxed mb-4" data-id="element-134">
                ¿Te ha pasado que comes "algo rápido" y después te sientes
                inflamada, con pesadez o sin energía?
              </p>
              <p className="text-lg text-white leading-relaxed mb-4" data-id="element-135">
                Este bono existe justamente para evitar eso.
              </p>
              <p className="text-xl text-white font-semibold leading-relaxed" data-id="element-136">
                Aquí no encontrarás snacks "fit" que engañan…
                <br data-id="element-137" />
                Aquí encontrarás alivio real.
              </p>
            </div>

            <div className="mb-8" data-id="element-138">
              <p className="text-lg text-[var(--color-navy)] leading-relaxed mb-6" data-id="element-139">
                Dentro de este bono recibirás{' '}
                <span className="font-bold text-[var(--color-coral)]" data-id="element-140">
                  30 snacks diseñados para desinflamar tu cuerpo
                </span>
                , calmar antojos intensos, estabilizar tu glucosa y devolverte
                esa energía ligera que tanto extrañas.
              </p>

              <div className="space-y-4 mb-8" data-id="element-141">
                <div className="bg-[var(--color-warm-beige)] p-6 rounded-xl" data-id="element-142">
                  <p className="text-lg text-[var(--color-navy)] leading-relaxed" data-id="element-143">
                    <span className="font-semibold" data-id="element-144">Snacks dulces</span> para
                    esos momentos en que tu cuerpo te pide un abrazo.
                  </p>
                </div>
                <div className="bg-[var(--color-warm-beige)] p-6 rounded-xl" data-id="element-145">
                  <p className="text-lg text-[var(--color-navy)] leading-relaxed" data-id="element-146">
                    <span className="font-semibold" data-id="element-147">Snacks salados</span> para
                    esos días en los que necesitas sostenerte sin caer en la
                    ansiedad.
                  </p>
                </div>
                <div className="bg-[var(--color-warm-beige)] p-6 rounded-xl" data-id="element-148">
                  <p className="text-lg text-[var(--color-navy)] leading-relaxed" data-id="element-149">
                    <span className="font-semibold" data-id="element-150">Snacks fáciles</span> para
                    cuando la vida va rápido y tú no quieres perder el control.
                  </p>
                </div>
              </div>
            </div>

            <h3 className="text-2xl font-bold text-[var(--color-navy)] mb-6 text-center" data-id="element-151">
              Todo está pensado para ayudarte a sentir:
            </h3>

            <div className="grid md:grid-cols-2 gap-4 mb-8" data-id="element-152">
              {['Menos hinchazón', 'Menos ansiedad', 'Menos culpa', 'Más ligereza', 'Más claridad mental', 'Más amor por tu propio cuerpo', 'Más saciedad'].map((benefit, index) => <div key={index} className="flex items-center gap-3 bg-[var(--color-warm-beige)] p-4 rounded-xl" data-id="element-153">
                  <Sparkles className="w-5 h-5 text-[var(--color-coral)] flex-shrink-0" data-id="element-154" />
                  <p className="text-[var(--color-navy)] font-medium" data-id="element-155">
                    {benefit}
                  </p>
                </div>)}
            </div>

            <div className="bg-[var(--color-warm-beige)] rounded-2xl p-6 mb-8" data-id="element-156">
              <p className="text-lg text-[var(--color-navy)] leading-relaxed text-center" data-id="element-157">
                Son opciones{' '}
                <span className="font-semibold" data-id="element-158">
                  prácticas, deliciosas y listas para usar
                </span>{' '}
                en el trabajo, en el carro, en la escuela, o cuando simplemente
                necesitas cuidarte un poquito más.
              </p>
            </div>

            <div className="bg-gradient-to-r from-[var(--color-coral)] to-[var(--color-coral-dark)] text-white p-8 rounded-2xl text-center mb-6" data-id="element-159">
              <p className="text-xl font-semibold leading-relaxed mb-4" data-id="element-160">
                Este bono es esa voz que te acompaña y te dice:
              </p>
              <p className="text-2xl font-bold leading-relaxed" data-id="element-161">
                "Sí puedes comer rico sin inflamarte.
                <br data-id="element-162" />
                Sí puedes sentirte ligera.
                <br data-id="element-163" />
                Sí puedes recuperar el control."
              </p>
            </div>

            <p className="text-xl text-[var(--color-navy)] text-center font-semibold" data-id="element-164">
              Y ahora está aquí, listo para que lo uses cada día.
            </p>
          </motion.div>
        </motion.div>
      </section>

      {/* Final CTA Section */}
      <section className="px-6 py-20 max-w-4xl mx-auto" data-id="element-165">
        <motion.div className="bg-gradient-to-br from-[var(--color-navy)] to-[var(--color-navy-light)] p-12 md:p-16 rounded-3xl text-white text-center shadow-2xl" {...fadeInUp} data-id="element-166">
          <h2 className="text-3xl md:text-5xl font-bold mb-8" data-id="element-167">
            Tienes dos caminos
          </h2>

          <div className="grid md:grid-cols-2 gap-8 mb-12 text-left" data-id="element-168">
            <div className="bg-white bg-opacity-10 p-6 rounded-xl backdrop-blur-sm" data-id="element-169">
              <p className="text-xl font-semibold mb-3" data-id="element-170">Camino 1:</p>
              <p className="text-lg opacity-90" data-id="element-171">
                Seguir inflamada, frustrada, sin entender qué pasa con tu
                cuerpo.
              </p>
            </div>
            <div className="bg-[var(--color-coral)] p-6 rounded-xl" data-id="element-172">
              <p className="text-xl font-semibold mb-3" data-id="element-173">Camino 2:</p>
              <p className="text-lg" data-id="element-174">
                Desinflamarte, recuperar tu energía y ver un vientre más plano
                en 7 días.
              </p>
            </div>
          </div>

          <div className="bg-white bg-opacity-10 p-8 rounded-2xl backdrop-blur-sm mb-10" data-id="element-175">
            <p className="text-2xl font-semibold mb-4" data-id="element-176">Garantía emocional</p>
            <p className="text-lg opacity-90 leading-relaxed" data-id="element-177">
              En 7 días te sentirás más ligera, más desinflamada, más energizada
              y más conectada contigo misma.
            </p>
          </div>

          <button type="button" onClick={e => {
          e.preventDefault();
          handleCheckout();
        }} className="bg-[var(--color-coral)] hover:bg-[var(--color-coral-dark)] text-white font-bold px-16 py-6 rounded-full text-xl transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:scale-105 mb-6" data-id="element-178">
            Sí, Quiero Transformarme
          </button>

          <p className="text-lg opacity-90" data-id="element-179">
            Tu cuerpo puede transformarse más rápido de lo que crees.
          </p>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="bg-[var(--color-navy)] text-white text-center py-8 px-6" data-id="element-180">
        <p className="opacity-75" data-id="element-181">
          © 2024 No es Gordura, es Inflamación. Todos los derechos reservados.
        </p>
      </footer>
    </div>;
}